import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../utils/axiosConfig';
import '../../styles/Reporting.css';
import { validateEmail } from '../../utils/validation';
import ErrorBoundary from '../ErrorBoundary';
import { InfoTooltip } from '../Common/InfoTooltip';

// Separate error boundary for report sections
const ReportSection = ({ children }) => (
  <ErrorBoundary fallback={<div className="bm-error-container">Unable to load report data</div>}>
    {children}
  </ErrorBoundary>
);

function ReportingTab() {
  const ITEMS_PER_PAGE = 10;
  // Separate state for different sections
  const [registrationData, setRegistrationData] = useState(null);
  const [checkinsData, setCheckinsData] = useState(null);
  
  // Separate loading states
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [isLoadingRegistrations, setIsLoadingRegistrations] = useState(true);
  const [isLoadingCheckins, setIsLoadingCheckins] = useState(true);
  const [vendorStats, setVendorStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [statsError, setStatsError] = useState(null);

  // Separate error states
  const [settingsError, setSettingsError] = useState(null);
  const [registrationsError, setRegistrationsError] = useState(null);
  const [checkinsError, setCheckinsError] = useState(null);
  
  // Pagination states
  const [registrationPage, setRegistrationPage] = useState(1);
  const [checkinPage, setCheckinPage] = useState(1);
  
  // Settings state
  const [reportSettings, setReportSettings] = useState({
    enabled: false,
    emails: [],
    time: "06:00"
  });
  const [newEmail, setNewEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState('');

  // Fetch data on mount and page changes
  useEffect(() => {
    fetchRegistrations();
  }, [registrationPage]);

  useEffect(() => {
    fetchCheckins();
  }, [checkinPage]);

  useEffect(() => {
    fetchReportSettings();
  }, []);

  useEffect(() => {
    fetchVendorStats();
  }, []);

  // const fetchReportSettings = async () => {
  //   setIsLoadingSettings(true);
  //   setSettingsError(null);
  //   try {
  //     const response = await axios.get('/api/vendors/reports/daily-settings');
  //     setReportSettings({
  //       enabled: response.data.daily_report_enabled || false,
  //       emails: response.data.daily_report_emails || [],
  //       time: response.data.daily_report_time || "06:00"
  //     });
  //   } catch (error) {
  //     console.error('Error fetching report settings:', error);
  //     setSettingsError('Failed to load report settings');
  //   } finally {
  //     setIsLoadingSettings(false);
  //   }
  // };

  const fetchReportSettings = async () => {
    setIsLoadingSettings(true);
    setSettingsError(null);
    try {
      const response = await axios.get('/api/vendors/reports/daily-settings');
      
      // Ensure time is properly formatted before setting state
      const timeValue = response.data.daily_report_time;
      let formattedTime = "06:00"; // Default time
      
      if (timeValue) {
        // Handle potential different time formats from the database
        if (typeof timeValue === 'string' && timeValue.includes(':')) {
          // If it's already a string like "06:00"
          formattedTime = timeValue;
        } else if (timeValue instanceof Date) {
          // If it's a date object
          const hours = timeValue.getHours().toString().padStart(2, '0');
          formattedTime = `${hours}:00`;
        }
      }
      
      setReportSettings({
        enabled: response.data.daily_report_enabled || false,
        emails: response.data.daily_report_emails || [],
        time: formattedTime // Set the properly formatted time
      });
      
      // Log the value for debugging
      console.log('Setting time from DB:', formattedTime);
    } catch (error) {
      console.error('Error fetching report settings:', error);
      setSettingsError('Failed to load report settings');
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const fetchRegistrations = async () => {
    setIsLoadingRegistrations(true);
    setRegistrationsError(null);
    try {
      const response = await axios.get(`/api/vendors/reports/registrations?page=${registrationPage}`);
      setRegistrationData(response.data);
      
      // If no data and not on first page, go back to first page
      if ((!response.data?.registrations || response.data.registrations.length === 0) && registrationPage > 1) {
        setRegistrationPage(1);
      }
    } catch (error) {
      console.error('Error fetching registrations:', error);
      setRegistrationsError('Failed to load registration data');
    } finally {
      setIsLoadingRegistrations(false);
    }
  };

  const fetchCheckins = async () => {
    setIsLoadingCheckins(true);
    setCheckinsError(null);
    try {
      const response = await axios.get(`/api/vendors/reports/checkins?page=${checkinPage}`);
      setCheckinsData(response.data);
    } catch (error) {
      console.error('Error fetching checkins:', error);
      setCheckinsError('Failed to load check-in data');
    } finally {
      setIsLoadingCheckins(false);
    }
  };

  const fetchVendorStats = async () => {
    setIsLoadingStats(true);
    setStatsError(null);
    try {
      const response = await axios.get('/api/vendors/reports/stats');
      setVendorStats(response.data);
    } catch (error) {
      console.error('Error fetching vendor stats:', error);
      setStatsError('Failed to load vendor statistics');
    } finally {
      setIsLoadingStats(false);
    }
  };

  const saveReportSettings = async () => {
    setIsSaving(true);
    try {
      await axios.put('/api/vendors/reports/daily-settings', {
        enabled: reportSettings.enabled,
        emails: reportSettings.emails,
        time: reportSettings.time
      });
      setSettingsMessage('Settings saved successfully');
      setTimeout(() => setSettingsMessage(''), 3000);
    } catch (error) {
      setSettingsMessage('Error saving settings');
      console.error('Error saving report settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const addEmail = (e) => {
    e.preventDefault();
    const validationErrors = validateEmail(newEmail);
    
    if (validationErrors.length > 0) {
      setSettingsMessage(`Invalid email: ${validationErrors.join('. ')}`);
      return;
    }
    
    if (!reportSettings.emails.includes(newEmail)) {
      setReportSettings((prev) => ({
        ...prev,
        emails: [...prev.emails, newEmail],
      }));
      setNewEmail('');
      setSettingsMessage('Email added successfully');
    } else {
      setSettingsMessage('Email already added');
    }
    setTimeout(() => setSettingsMessage(''), 3000);
  };

  const removeEmail = (emailToRemove) => {
    setReportSettings(prev => ({
      ...prev,
      emails: prev.emails.filter(email => email !== emailToRemove)
    }));
  };

  // Render helper functions
  const renderSettingsSection = () => (
    <div className="bm-card">
      <div className="bm-card-header">
        <h2>Daily Report Settings</h2>
      </div>
      <div className="bm-card-body">
        {isLoadingSettings ? (
          <div className="bm-loading-inline">
            <div className="bm-spinner"></div>
            <span>Loading settings...</span>
          </div>
        ) : settingsError ? (
          <div className="bm-error-container">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <p>{settingsError}</p>
          </div>
        ) : (
          <div className="bm-form">
            <div className="bm-form-group">
              <label className="bm-form-label toggle-label">
                <div className="lp-switch">
                  <input
                    type="checkbox"
                    checked={reportSettings.enabled}
                    onChange={(e) => setReportSettings(prev => ({
                      ...prev,
                      enabled: e.target.checked
                    }))}
                  />
                  <span className="lp-slider"></span>
                </div>
                <span>Enable Daily Reports</span>
                {reportSettings.enabled && reportSettings.emails.length === 0 && (
                  <span className="bm-validation-message">Please add at least one email address</span>
                )}
              </label>
            </div>

            <div className="bm-form-group">
              <label className="bm-form-label">
                Report Time 
                <InfoTooltip 
                  text="Can only be set and sent once per day on the hour" 
                  variant="vendor"
                  position="right"
                />
              </label>
              <div className="bm-select-container">
                <select
                  className="bm-select-input"
                  value={reportSettings.time}
                  onChange={(e) => setReportSettings(prev => ({
                    ...prev,
                    time: e.target.value
                  }))}
                >
                  {Array.from({ length: 24 }, (_, i) => {
                    const hour = i.toString().padStart(2, '0');
                    return <option key={hour} value={`${hour}:00`}>{hour}:00</option>;
                  })}
                </select>
                <div className="bm-select-arrow">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
              </div>
            </div>

            <div className="bm-form-group">
              <label className="bm-form-label">Report Recipients</label>
              <div className="bm-cc-emails-list">
                {reportSettings.emails?.length > 0 ? (
                  reportSettings.emails.map(email => (
                    <div key={email} className="bm-cc-email-tag">
                      <span>{email}</span>
                      <button 
                        onClick={() => removeEmail(email)}
                        className="bm-remove-email-btn"
                        aria-label={`Remove ${email}`}
                      >
                        ×
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="bm-empty-state email-empty-state">
                    <p>No recipients added</p>
                  </div>
                )}
              </div>
              <form onSubmit={addEmail} className="bm-cc-emails-input-container">
                <input
                  type="email"
                  className="bm-input"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Add email address"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addEmail(e);
                    }
                  }}
                />
                <button type="submit" className="bm-add-email-btn">
                  Add
                </button>
              </form>
            </div>

            <div className="bm-form-actions">
              <button 
                onClick={saveReportSettings}
                disabled={isSaving || (reportSettings.enabled && !reportSettings.emails?.length)}
                className="bm-button primary"
              >
                {isSaving ? (
                  <>
                    <div className="bm-spinner-sm"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Save Settings</span>
                )}
              </button>
            </div>
            
            {settingsMessage && (
              <div className={`bm-toast ${settingsMessage.includes('Error') ? 'error' : 'success'}`}>
                <div className="bm-toast-content">
                  <div className={`bm-toast-icon ${settingsMessage.includes('Error') ? 'error' : 'success'}`}>
                    {settingsMessage.includes('Error') ? '!' : '✓'}
                  </div>
                  <span className="bm-toast-message">{settingsMessage}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderStatsSection = () => (
    <ReportSection>
      <div className="bm-card">
        <div className="bm-card-header">
          <h2>Overall Statistics</h2>
        </div>
        <div className="bm-card-body">
          {isLoadingStats ? (
            <div className="bm-loading-inline">
              <div className="bm-spinner"></div>
              <span>Loading statistics...</span>
            </div>
          ) : statsError ? (
            <div className="bm-error-container">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <p>{statsError}</p>
            </div>
          ) : vendorStats && (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <h3>Total Registrations</h3>
                  <p className="stat-number">{vendorStats.totals.total_registrations}</p>
                </div>
                <div className="stat-card">
                  <h3>Total Check-ins</h3>
                  <p className="stat-number">{vendorStats.totals.total_checkins}</p>
                </div>
                <div className="stat-card">
                  <h3>Average Days Between Check-ins</h3>
                  <p className="stat-number">
                    {vendorStats.totals.avg_days_between_checkins || 'N/A'}
                    {vendorStats.totals.avg_days_between_checkins && ' days'}
                  </p>
                </div>
              </div>
    
              <div className="bm-section-title">
                <div className="bm-section-heading">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                  <h3>Top 10 Most Active Customers</h3>
                </div>
              </div>
              
              <div className="report-table-container">
                <table className="bm-table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Total Check-ins</th>
                      <th>Cards Completed</th>
                      <th>Avg. Days Between Visits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendorStats.topCustomers.map((customer, index) => (
                      <tr key={index}>
                        <td data-label="Customer">{customer.full_name}</td>
                        <td data-label="Total Check-ins">{customer.total_checkins}</td>
                        <td data-label="Cards Completed">{customer.total_cards_completed}</td>
                        <td data-label="Avg. Days Between Visits">{customer.avg_days_between_visits}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
    
              <div className="bm-section-title">
                <div className="bm-section-heading">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="3" width="20" height="18" rx="2"></rect>
                    <line x1="8" y1="21" x2="8" y2="3"></line>
                    <line x1="16" y1="21" x2="16" y2="3"></line>
                    <line x1="2" y1="9" x2="22" y2="9"></line>
                    <line x1="2" y1="15" x2="22" y2="15"></line>
                  </svg>
                  <h3>Monthly Activity</h3>
                </div>
              </div>
              
              <div className="report-table-container">
                <table className="bm-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Registrations</th>
                      <th>Check-ins</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendorStats.monthly.map((month, index) => (
                      <tr key={index}>
                        <td data-label="Month">{new Date(month.month).toLocaleDateString(undefined, { year: 'numeric', month: 'long' })}</td>
                        <td data-label="Registrations">{month.registrations}</td>
                        <td data-label="Check-ins">{month.checkins}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </ReportSection>
  );

  const renderRegistrationsSection = () => (
    <ReportSection>
      <div className="bm-card">
        <div className="bm-card-header">
          <h2>Registration Report</h2>
        </div>
        <div className="bm-card-body">
          {isLoadingRegistrations ? (
            <div className="bm-loading-inline">
              <div className="bm-spinner"></div>
              <span>Loading registrations...</span>
            </div>
          ) : registrationsError ? (
            <div className="bm-error-container">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <p>{registrationsError}</p>
            </div>
          ) : (
            <>
              <div className="daily-summary">
                {registrationData?.dailySummary?.length > 0 ? (
                  registrationData.dailySummary.map(day => (
                    <div key={day.registration_date} className="daily-stat">
                      <span>{new Date(day.registration_date).toLocaleDateString()}</span>
                      <span className="registration-count">
                        {day.registration_count} new
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="bm-empty-state">
                    <p>No registration data available</p>
                  </div>
                )}
              </div>
    
              <div className="bm-section-title">
                <div className="bm-section-heading">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                  <h3>Recent Registrations</h3>
                </div>
              </div>
              
              {!registrationData?.registrations?.length ? (
                <div className="bm-empty-state">
                  <p>No registrations found</p>
                </div>
              ) : (
                <>
                  <div className="report-table-container">
                    <table className="bm-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {registrationData.registrations.map(reg => (
                          <tr key={reg.id}>
                          <td data-label="Name">{reg.full_name}</td>
                          <td data-label="Email">{reg.email}</td>
                          <td data-label="Phone">{reg.phone}</td>
                          <td data-label="Date">{new Date(reg.enrolled_date).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
    
                  {registrationData.totalPages > 1 && registrationData.registrations.length > 0 && (
                    <div className="bm-pagination">
                      <button 
                        onClick={() => setRegistrationPage(p => p - 1)}
                        className="bm-pagination-btn"
                        disabled={registrationPage <= 1}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                        Previous
                      </button>
                      <span className="bm-pagination-info">Page {registrationPage} of {registrationData.totalPages}</span>
                      <button 
                        onClick={() => setRegistrationPage(p => p + 1)}
                        className="bm-pagination-btn"
                        disabled={registrationPage >= registrationData.totalPages || registrationData.registrations.length < ITEMS_PER_PAGE}
                      >
                        Next
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </ReportSection>
  );

  const renderCheckinsSection = () => (
    <ReportSection>
      <div className="bm-card">
        <div className="bm-card-header">
          <h2>Daily Check-ins</h2>
        </div>
        <div className="bm-card-body">
          {isLoadingCheckins ? (
            <div className="bm-loading-inline">
              <div className="bm-spinner"></div>
              <span>Loading check-ins...</span>
            </div>
          ) : checkinsError ? (
            <div className="bm-error-container">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <p>{checkinsError}</p>
            </div>
          ) : (
            <>
              <div className="daily-summary">
                {checkinsData?.dailySummary?.length > 0 ? (
                  checkinsData.dailySummary.map(day => (
                    <div key={day.checkin_date} className="daily-stat">
                      <span>{new Date(day.checkin_date).toLocaleDateString()}</span>
                      <span className="checkin-count">{day.checkin_count} check-ins</span>
                    </div>
                  ))
                ) : (
                  <div className="bm-empty-state">
                    <p>No check-ins data available</p>
                  </div>
                )}
              </div>

              {checkinsData?.recentCheckins?.length > 0 && (
                <>
                  <div className="bm-section-title">
                    <div className="bm-section-heading">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                        <path d="M18 12a2 2 0 0 0 0 4h4v-4z"></path>
                      </svg>
                      <h3>Recent Check-ins</h3>
                    </div>
                  </div>
                  
                  <div className="report-table-container">
                    <table className="bm-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Program</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {checkinsData.recentCheckins.map((checkin, index) => (
                          <tr key={`${checkin.full_name}-${checkin.checkin_date}-${index}`}>
                                <td data-label="Name">{checkin.full_name}</td>
                                <td data-label="Program">{checkin.program_name}</td>
                                <td data-label="Date">{new Date(checkin.checkin_date).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {checkinsData.totalPages > 1 && checkinsData.recentCheckins.length > 0 && (
                    <div className="bm-pagination">
                      <button 
                        onClick={() => setCheckinPage(p => p - 1)}
                        className="bm-pagination-btn"
                        disabled={checkinPage <= 1}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                        Previous
                      </button>
                      <span className="bm-pagination-info">Page {checkinPage} of {checkinsData.totalPages}</span>
                      <button 
                        onClick={() => setCheckinPage(p => p + 1)}
                        className="bm-pagination-btn"
                        disabled={checkinPage >= checkinsData.totalPages || checkinsData.recentCheckins.length < ITEMS_PER_PAGE}
                      >
                        Next
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </ReportSection>
  );

  // Main render
  return (
    <div className="bm-container">
      <div className="bm-header">
        <div className="bm-header-content">
          <h1>Reports & Analytics</h1>
          <p className="bm-header-subtitle">View customer activity and configure daily reports</p>
        </div>
      </div>
      
      <ErrorBoundary>
        {renderSettingsSection()}
        {renderStatsSection()}
        {renderRegistrationsSection()}
        {renderCheckinsSection()}
      </ErrorBoundary>
    </div>
  );
}

export default ReportingTab;